import { defineStore } from "pinia";
import {
    EtfTrailingPerformance,
    FetchState, FundType,
    GenericBarChartDataPoint,
    GenericGraphPoint,
    GenericMultiColumnBarChartDataPoint,
    InstrumentPerformanceData,
    InstrumentPerformanceGraphTimePeriod,
    InstrumentPerformanceResponseModel,
    InstrumentPerformanceTimePeriod,
    MorningstarInstrumentPerformanceGraphRequestArgs,
    MorningstarInstrumentPerformanceState
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import moment from "moment";
import * as drivewealthService from "@/shared/api/drivewealthService";
import { roundToTwoDecimals } from "@/shared/mathHelper";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import { useDrivewealthStore } from "@/shared/store/drivewealth";

const formatEtfBarChartDataPoint = (dataPoint: EtfTrailingPerformance): GenericMultiColumnBarChartDataPoint => {
    return {
        label: getBarChartLabel(dataPoint.timePeriod),
        value1: roundToTwoDecimals(dataPoint.benchmarkPerformanceReturn),
        value2: roundToTwoDecimals(dataPoint.trailingPerformanceReturn),
        position: getPositionForColumn(dataPoint.timePeriod),
    };
};

const getPositionForColumn = (timePeriod: InstrumentPerformanceTimePeriod): number => {
    switch (timePeriod) {
        case InstrumentPerformanceTimePeriod.Months0:
            return 1;
        case InstrumentPerformanceTimePeriod.Months1:
            return 2;
        case InstrumentPerformanceTimePeriod.Months3:
            return 3;
        case InstrumentPerformanceTimePeriod.Months6:
            return 4;
        case InstrumentPerformanceTimePeriod.Months12:
            return 5;
        case InstrumentPerformanceTimePeriod.Months36:
            return 6;
        case InstrumentPerformanceTimePeriod.Months60:
            return 7;
        default:
            return 0;
    }
};

const getBarChartLabel = (timePeriod: InstrumentPerformanceTimePeriod): string => {
    switch (timePeriod) {
        case InstrumentPerformanceTimePeriod.Months0:
            return "NAV 1-Day";
        case InstrumentPerformanceTimePeriod.Months1:
            return "1M";
        case InstrumentPerformanceTimePeriod.Months3:
            return "3M";
        case InstrumentPerformanceTimePeriod.Months6:
            return "6M";
        case InstrumentPerformanceTimePeriod.Months12:
            return "1Y";
        case InstrumentPerformanceTimePeriod.Months36:
            return "3Y";
        case InstrumentPerformanceTimePeriod.Months60:
            return "5Y";
        default:
            return "N/A";
    }
};

export const initialState: MorningstarInstrumentPerformanceState = {
    selectedTimePeriod: InstrumentPerformanceGraphTimePeriod.Weeks1,
};

const useInstrumentPerformanceDataSubStore = defineStore("morningstar_InstrumentPerformanceData", () => {
    return templatedFetchKeyCollectionStore<InstrumentPerformanceData, InstrumentPerformanceData>(
        drivewealthService.getInstrumentPerformanceData,
    );
});

const useInstrumentPerformanceGraphSubStore = defineStore("morningstar_InstrumentPerformanceGraph", () => {
    return templatedFetchKeyCollectionStore<InstrumentPerformanceResponseModel[], InstrumentPerformanceResponseModel[]>(
        drivewealthService.getInstrumentPerformanceGraphData,
    );
});

const instrumentPerformanceItemKeyFormatter = (args: MorningstarInstrumentPerformanceGraphRequestArgs): string => {
    return `${args.stockSymbol}_${args.dateStart}_${args.dateEnd}`;
};

export const useInstrumentPerformanceDataStore = defineStore("instrumentPerformanceData", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("instrumentPerformanceData"),
    },
    state: (): MorningstarInstrumentPerformanceState => (_.cloneDeep(initialState)),
    getters: {
        instrumentPerformanceDataSubStore: () => useInstrumentPerformanceDataSubStore(),
        instrumentPerformanceGraphSubStore: () => useInstrumentPerformanceGraphSubStore(),
        instrumentPerformanceLoaded (): (symbol: string) => boolean {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceDataSubStore.fetchStates[symbol];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loaded;
                }

                return false;
            };
        },
        instrumentPerformanceLoading (): (symbol: string) => boolean {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceDataSubStore.fetchStates[symbol];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loading;
                }

                return false;
            };
        },
        instrumentPerformanceData (): (symbol: string) => InstrumentPerformanceData {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceDataSubStore.items[symbol];

                if (performanceLookup) {
                    return performanceLookup;
                }

                return false;
            };
        },
        fundPerformanceGraphLoaded (): (args: MorningstarInstrumentPerformanceGraphRequestArgs) => boolean {
            return (args: MorningstarInstrumentPerformanceGraphRequestArgs) => {
                const itemKey = instrumentPerformanceItemKeyFormatter(args);
                const lookup = this.instrumentPerformanceGraphSubStore.fetchStates[itemKey];

                if (lookup) {
                    return lookup === FetchState.Loaded;
                }

                return false;
            };
        },
        fundPerformanceGraphPoints (): (args: MorningstarInstrumentPerformanceGraphRequestArgs) => GenericGraphPoint[] {
            return (args: MorningstarInstrumentPerformanceGraphRequestArgs) => {
                const itemKey = instrumentPerformanceItemKeyFormatter(args);
                const lookup = this.instrumentPerformanceGraphSubStore.items[itemKey];

                if (lookup) {
                    const instrumentDetail = useDrivewealthStore().instrumentDetailBySymbol(args.stockSymbol);

                    const mappedLookups = lookup.map(x => {
                        return {
                            date: new Date(x.date),
                            value: x.stockPrice,
                        };
                    });

                    // if we are using 1 day date selection, do not manually add todays current price
                    const fiveDaysPreviousDate = moment().add(-5, "days");
                    const dateStart = moment(args.dateStart);
                    if (dateStart.isAfter(fiveDaysPreviousDate)) {
                        return [...mappedLookups];
                    }

                    return [
                        ...mappedLookups,
                        { date: moment().toDate(), value: instrumentDetail.currentPrice }
                    ];
                }

                return [];
            };
        },
        previousDayClosePrice (): (symbol: string) => number {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceData(symbol);

                if (performanceLookup) {

                    if (performanceLookup.fundType !== FundType.Equity) {
                        return performanceLookup.etfClosePrice;
                    }

                    const data = performanceLookup.trailingPerformances;
                    const selectedData = _.find(data, { returnType: "GbPostTax" });

                    if (!selectedData) {
                        return 0;
                    }

                    const yesterdayReturn = _.find(selectedData.return, x => x.timePeriod === InstrumentPerformanceTimePeriod.ClosePrice);

                    return yesterdayReturn.value;
                }

                return 0;
            };
        },
        etfBarChartData (): (symbol: string) => GenericMultiColumnBarChartDataPoint[] {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceData(symbol);

                if (performanceLookup && !!performanceLookup.etfTrailingPerformances) {
                    const etfReturnsData = performanceLookup.etfTrailingPerformances;

                    const mappedData = _.map(etfReturnsData, item => {
                        return formatEtfBarChartDataPoint(item);
                    });

                    return _.sortBy(mappedData, "position");
                }
                return [];
            };
        },
        trailingPerformancesBarChartData (): (symbol: string) => GenericBarChartDataPoint[] {
            return (symbol: string) => {
                const performanceLookup = this.instrumentPerformanceData(symbol);

                if (performanceLookup && !!performanceLookup.trailingReturns) {
                    const trailingReturnsData = performanceLookup.trailingReturns;

                    return [
                        {
                            label: "1 D",
                            value: trailingReturnsData.trailing1DayReturn
                        },
                        {
                            label: "1 W",
                            value: trailingReturnsData.trailing1WeekReturn
                        },
                        {
                            label: "1 M",
                            value: trailingReturnsData.trailing1MonthReturn
                        },
                        {
                            label: "3 M",
                            value: trailingReturnsData.trailing3MonthReturn
                        },
                        {
                            label: "1 Y",
                            value: trailingReturnsData.trailing1YearReturn
                        },
                        {
                            label: "3 Y",
                            value: trailingReturnsData.trailing3YearReturn
                        },
                        {
                            label: "5 Y",
                            value: trailingReturnsData.trailing5YearReturn
                        },
                    ];
                }

                return [];
            };
        },
    },
    actions: {
        setSelectedTimePeriod (timePeriod: InstrumentPerformanceGraphTimePeriod) {
            this.selectedTimePeriod = timePeriod;
        },
        async getInstrumentPerformanceGraphData (
            args: MorningstarInstrumentPerformanceGraphRequestArgs
        ): Promise<void> {
            const itemKey = instrumentPerformanceItemKeyFormatter(args);
            return useInstrumentPerformanceGraphSubStore().fetchKeyedApiCall(
                { username: args.username, partnerId: args.partnerId },
                itemKey,
                { stockSymbol: args.stockSymbol, dateStart: args.dateStart, dateEnd: args.dateEnd },
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getInstrumentPerformanceData (
            { stockSymbol, username, partnerId, accountId }:
            {
                stockSymbol: string,
                username?: string,
                partnerId?: number,
                accountId?: number
            }
        ): Promise<void> {
            return useInstrumentPerformanceDataSubStore().fetchKeyedApiCall(
                { username, partnerId },
                stockSymbol,
                { stockSymbol, accountId }
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});